/**
 * notify — Sprint 4C (background tasks)
 * Thin wrapper over ServiceWorker notifications for background-task progress and
 * completion. Reusing the same `tag` UPDATES the notification in place, so a long
 * job can show live progress ("Procesando fotos 12/40…") without stacking.
 *
 * Degrades silently: a no-op when notifications are unsupported or not granted —
 * the in-app completion chip (BackgroundTaskLayer) is the fallback. Permission is
 * requested lazily on the first task, never at onboarding.
 */
const ICON = (import.meta.env.BASE_URL || '/') + 'assets/logo.png';

/** Ask for notification permission once, lazily. Returns true if granted. */
export async function ensureNotifyPermission() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const res = await Notification.requestPermission();
    return res === 'granted';
  } catch {
    return false;
  }
}

async function show(tag, title, body, opts = {}) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(title, {
      body,
      tag,
      icon: ICON,
      badge: ICON,
      ...opts,
    });
  } catch { /* notifications are best-effort */ }
}

/** Update the (single, tagged) progress notification. Silent — no buzz per tick. */
export function notifyProgress(tag, title, body) {
  return show(tag, title, body, { silent: true, requireInteraction: false });
}

/** Final notification when a task finishes. */
export function notifyDone(tag, title, body) {
  return show(tag, title, body, { requireInteraction: false });
}

/** Clear a tagged notification (e.g. once the user acted on the result). */
export async function clearNotification(tag) {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const list = await reg.getNotifications({ tag });
    list.forEach(n => n.close());
  } catch { /* ignore */ }
}
